"use client";

import { useEffect, useState, type HTMLAttributes } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff } from "lucide-react";
import { signIn as signInWithProvider } from "next-auth/react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useForm } from "react-hook-form";
import { FaGithub, FaGoogle, FaMicrosoft } from "react-icons/fa6";
import { PiSpinner } from "react-icons/pi";
import type * as z from "zod";

import { signIn } from "@/actions/sign-in";
import FormError from "@/components/atoms/form-error";
import { AuthPageBrand } from "@/components/auth/auth-page-brand";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  clearAuthSessionBeforeSignIn,
} from "@/lib/cookies.client";
import { cn } from "@/lib/utils";
import { SignInSchema } from "@/schemas/sign-in";
import { sanitizePostAuthCallbackUrl } from "@/routes";

type SchemaType = typeof SignInSchema;
type FormValues = z.infer<SchemaType>;

const STEP_SETTLE_MS = 150;

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function getSafeCallbackUrl(raw: string | null): string {
  return sanitizePostAuthCallbackUrl(raw);
}

interface SignInFormProps extends HTMLAttributes<HTMLDivElement> {
  orgName?: string;
  inviteToken?: string;
  email?: string;
}

export default function SignInForm({
  className,
  orgName,
  inviteToken,
  email,
}: Readonly<SignInFormProps>) {
  const searchParams = useSearchParams();
  const callbackUrl = getSafeCallbackUrl(searchParams.get("callbackUrl"));
  const showEmailPasswordOption =
    process.env.NEXT_PUBLIC_SIGN_IN_EMAIL_PASSWORD_ENABLED === "true";
  const showSignUpOption =
    process.env.NEXT_PUBLIC_SIGN_UP_ENABLED === "true";
  const urlError = (() => {
    const error = searchParams.get("error");
    if (error === "OAuthAccountNotLinked") {
      return "Email is already used with another provider";
    }
    if (error === "SignUpDisabled") {
      return "Sign up is currently closed. Request an invite to join.";
    }
    if (error === "CredentialsSignin") {
      return "Invalid email or password";
    }
    return "";
  })();
  const [emailSectionOpen, setEmailSectionOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | undefined>();
  // const [success, setSuccess] = useState<string | undefined>();
  const [, setSuccess] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const form = useForm<FormValues>({
    resolver: zodResolver(SignInSchema),
    defaultValues: {
      email: email || "",
      password: "",
    },
  });

  useEffect(() => {
    if (!showEmailPasswordOption) {
      return;
    }
    if (searchParams.get("error") === "OAuthAccountNotLinked") {
      setEmailSectionOpen(true);
      return;
    }
    if (email) {
      setEmailSectionOpen(true);
    }
  }, [showEmailPasswordOption, searchParams, email]);

  const clearCookiesAndDelay = async (): Promise<void> => {
    await clearAuthSessionBeforeSignIn();
  };

  const handleOAuthSignIn = async (
    provider: "google" | "github" | "microsoft-entra-id",
    oauthCallbackUrl: string,
  ): Promise<void> => {
    // Store invite token in sessionStorage if provided (for OAuth invite flow)
    if (inviteToken && email) {
      sessionStorage.setItem(`invt_${btoa(email)}`, inviteToken);
    }
    await clearCookiesAndDelay();
    await signInWithProvider(provider, { callbackUrl: oauthCallbackUrl });
  };

  async function onSubmit(data: FormValues): Promise<void> {
    setError(undefined);
    setSuccess(undefined);
    setIsSubmitting(true);

    try {
      // Store invite token in sessionStorage if provided (before clearing)
      // This allows AcceptExistingUserInvite to pick it up after sign-in
      if (inviteToken && data.email) {
        sessionStorage.setItem(`invt_${btoa(data.email)}`, inviteToken);
      } else {
        // Only clear if no invite token (preserve invite token if present)
        sessionStorage.clear();
      }

      // Step 1: server action — .NET `/login`, net + bridge cookies
      const response = await signIn(data);

      if (response?.error) {
        setError(response?.error);
        return;
      }

      if (response?.success) {
        setSuccess(response.success);
        return;
      }

      // Step 1 cleared stale JWT server-side; brief pause before step 2
      await wait(STEP_SETTLE_MS);

      // Step 2: Auth.js session (trusts step-1 bridge/net cookies in authorize)
      const nextAuthResult = await signInWithProvider("credentials", {
        email: data.email,
        password: data.password,
        redirect: false,
      });

      if (nextAuthResult?.error || nextAuthResult?.ok === false) {
        setError(
          `Authentication failed: ${nextAuthResult?.error ?? "Unknown error"}`,
        );
        return;
      }

      globalThis.location.assign(response?.redirect || callbackUrl);
    } catch (submitError) {
      console.error("NextAuth sign-in error:", submitError);
      setError("Authentication failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  let cardDescription: string;
  if (orgName) {
    cardDescription = "Sign in to join the team";
  } else if (showEmailPasswordOption && emailSectionOpen) {
    cardDescription = "Sign In with a provider or your email and password";
  } else {
    cardDescription = "Sign In with Google, Microsoft, or GitHub";
  }

  const registerHref =
    callbackUrl !== "/dashboard"
      ? `/auth/register?callbackUrl=${encodeURIComponent(callbackUrl)}`
      : "/auth/register";

  return (
    <div className="animate-fadeInDown flex flex-col items-center">
      <Card className="w-full max-w-md overflow-hidden rounded-none border-0 bg-background shadow-2xl backdrop-blur-sm sm:rounded-xl">
        <CardHeader className="text-center">
          <AuthPageBrand />
          <CardTitle className="text-2xl font-semibold tracking-tight">
            {orgName ? `Join ${orgName} Team` : "Sign In"}
          </CardTitle>
          <CardDescription>{cardDescription}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className={cn("grid gap-6", className)}>
            {urlError ? <FormError message={urlError} /> : null}
            <div className="flex w-full flex-col gap-2">
              <Button
                type="button"
                title="Sign In with Google"
                variant="default"
                className="h-9 w-full justify-center gap-3"
                onClick={() => handleOAuthSignIn("google", callbackUrl)}
              >
                <FaGoogle className="size-4 shrink-0" aria-hidden />
                <span>Sign In with Google</span>
              </Button>
              <Button
                type="button"
                title="Sign In with Microsoft"
                variant="default"
                className="h-9 w-full justify-center gap-3"
                onClick={() =>
                  handleOAuthSignIn("microsoft-entra-id", callbackUrl)
                }
              >
                <FaMicrosoft className="size-4 shrink-0" aria-hidden />
                <span>Sign In with Microsoft</span>
              </Button>
              <Button
                type="button"
                title="Sign In with GitHub"
                variant="default"
                className="h-9 w-full justify-center gap-3"
                onClick={() => handleOAuthSignIn("github", callbackUrl)}
              >
                <FaGithub className="size-4 shrink-0" aria-hidden />
                <span>Sign In with GitHub</span>
              </Button>
            </div>
            {showEmailPasswordOption && !emailSectionOpen ? (
              <button
                type="button"
                className="text-center text-sm font-medium text-primary underline underline-offset-4 hover:text-primary/90"
                onClick={() => setEmailSectionOpen(true)}
              >
                Sign in with email
              </button>
            ) : null}
            {showEmailPasswordOption && emailSectionOpen ? (
              <>
                <div className="relative w-full">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">
                      Email
                    </span>
                  </div>
                </div>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)}>
                    <div className="grid gap-4">
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                type="email"
                                placeholder="Enter your email address"
                                autoCapitalize="none"
                                autoComplete="email"
                                autoCorrect="off"
                                disabled={isSubmitting}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="password"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Password</FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Input
                                  {...field}
                                  type={showPassword ? "text" : "password"}
                                  placeholder="Enter your password"
                                  autoCapitalize="none"
                                  autoComplete="password"
                                  autoCorrect="off"
                                  disabled={isSubmitting}
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                  onClick={togglePasswordVisibility}
                                  aria-label={
                                    showPassword
                                      ? "Hide password"
                                      : "Show password"
                                  }
                                >
                                  {showPassword ? (
                                    <EyeOff className="size-4 text-gray-500" />
                                  ) : (
                                    <Eye className="size-4 text-gray-500" />
                                  )}
                                </Button>
                              </div>
                            </FormControl>
                            <div className="flex justify-end">
                              <Link
                                href="/auth/forgot-password"
                                className="text-xs text-primary hover:underline"
                              >
                                Forgot password?
                              </Link>
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormError message={error} />
                      <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting && (
                          <PiSpinner className="mr-2 size-4 animate-spin" />
                        )}
                        Sign In
                      </Button>
                    </div>
                  </form>
                </Form>
              </>
            ) : null}
            {!orgName && showSignUpOption ? (
              <>
                <div className="relative w-full">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">
                      Or continue with
                    </span>
                  </div>
                </div>
                <Button variant="outline" className="h-9 w-full" asChild>
                  <a href={registerHref}>Create an account</a>
                </Button>
              </>
            ) : null}
          </div>
        </CardContent>
        <CardFooter className="flex flex-col space-y-4">
          <p className="w-full max-w-none text-center text-xs leading-relaxed text-muted-foreground">
            By signing in and using our application and services, you agree to
            our{" "}
            <Link
              href={`https://kriyastream.com/terms-of-service`}
              className="underline underline-offset-4 hover:text-primary"
              target="_blank"
            >
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link
              href={`https://kriyastream.com/privacy-policy`}
              className="underline underline-offset-4 hover:text-primary"
              target="_blank"
            >
              Privacy Policy
            </Link>
            .
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}
